import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiRequestError, type QuestionAggregateDto, type QuestionInput, type TestAttemptDto } from "@mmm/contracts";
import { QuestionCard } from "../../../components/tests/QuestionCard.js";
import { QuestionEditor } from "../../../components/tests/QuestionEditor.js";
import { ListRow } from "../../../components/molecules/ListRow.js";
import { ExportPreview } from "../../../components/organisms/ExportPreview.js";
import { TestDocumentPreview } from "../../../components/molecules/TestDocumentPreview.js";
import { SegmentedControl } from "../../../components/ui/SegmentedControl.js";
import { updateAITest, replaceAITestQuestions, deleteAITest, startTestAttempt } from "../../../api/commands.js";
import { downloadTestDocument } from "../../../api/files.js";
import { getAITest } from "../../../api/queries.js";
import { useQuery } from "../../../api/useQuery.js";
import { useConfirm } from "../../../hooks/useConfirm.js";
import { useAutosave } from "../../../hooks/useAutosave.js";
import { LoadingRows, QueryStatus } from "../../../components/ui/LoadingPlaceholders.js";
import { useSubjectContext } from "../useSubjectContext.js";
import { Badge, Button, Checkbox, ErrorState, Icon, type MessageKey, useT } from "@mmm/ui";
type TestView = "test" | "answers";

function TestTitleField({ testId, title, chapterId }: { testId: string; title: string; chapterId: string | null }) {
  const [draft, setDraft] = useState(title);
  const t = useT();
  const { saveNow } = useAutosave(
    draft,
    (next) => updateAITest({ testId, title: next, chapterId }),
    { key: `test-title:${testId}` }
  );
  return (
    <input
      className="mmm-input mmm-test-title"
      aria-label={t("common.name")}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={saveNow}
    />
  );
}

// Message keys, translated at render so the control follows the UI language.
const VIEW_OPTIONS: Array<{ value: TestView; labelKey: MessageKey }> = [
  { value: "test", labelKey: "eventType.test" },
  { value: "answers", labelKey: "tests.answers" },
];

const ATTEMPT_STATUS: Record<TestAttemptDto["status"], { labelKey: MessageKey; tone: "success" | "neutral" }> = {
  inProgress: { labelKey: "tests.statusInProgress", tone: "neutral" },
  submitted: { labelKey: "tests.statusAwaitingMarking", tone: "neutral" },
  marked: { labelKey: "tests.statusMarked", tone: "success" },
  abandoned: { labelKey: "tests.statusAbandoned", tone: "neutral" },
};

function AttemptRow({
  attempt,
  status,
  subjectId,
  testId,
  navigate,
}: {
  attempt: TestAttemptDto;
  status: { labelKey: MessageKey; tone: "success" | "neutral" };
  subjectId: string;
  testId: string;
  navigate: (to: string) => void;
}) {
  const t = useT();
  const action =
    attempt.status === "inProgress" ? (
      <Button variant="text" onClick={() => navigate(`/subjects/${subjectId}/tests/${testId}/attempts/${attempt.id}`)}>
        {t("tests.resume")}
      </Button>
    ) : attempt.status === "submitted" ? (
      <Button variant="text" onClick={() => navigate(`/subjects/${subjectId}/tests/${testId}/attempts/${attempt.id}/mark`)}>
        {t("tests.mark")}
      </Button>
    ) : attempt.status === "marked" ? (
      <Button variant="text" onClick={() => navigate(`/subjects/${subjectId}/tests/${testId}/attempts/${attempt.id}/review`)}>
        {t("tests.review")}
      </Button>
    ) : null;

  return (
    <ListRow
      className="mmm-attempt-row"
      title={<span className="mmm-attempt-name">{t("tests.attemptNumber", { number: attempt.attemptNumber })}</span>}
      meta={<Badge tone={status.tone}>{t(status.labelKey)}</Badge>}
      trailing={
        <>
          <span className="mmm-attempt-score">
            {attempt.status === "marked" && attempt.scorePercent !== null
              ? t("tests.percentValue", { percent: attempt.scorePercent })
              : ""}
          </span>
          {action}
        </>
      }
    />
  );
}

function toEditableQuestions(questions: QuestionAggregateDto[]): QuestionInput[] {
  return questions.map((q) => ({
    questionNumber: q.questionNumber,
    type: q.type,
    prompt: q.prompt,
    correctAnswer: q.correctAnswer,
    explanation: q.explanation,
    answerOptions: q.answerOptions.map(({ sortOrder, text, isCorrect }) => ({ sortOrder, text, isCorrect })),
    rubricItems: q.rubricItems.map(({ sortOrder, text, points }) => ({ sortOrder, text, points })),
  }));
}

export function AITestEditorScreen() {
  const [includeMemorandum, setIncludeMemorandum] = useState(false);
  const { subject } = useSubjectContext();
  const { testId = "" } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const t = useT();

  const testQuery = useQuery(getAITest, { testId });
  const { data: aggregate } = testQuery;

  // The test shows without answers by default; the memo is a separate view.
  const [view, setView] = useState<TestView>("test");
  const [editing, setEditing] = useState(false);
  const [draftQuestions, setDraftQuestions] = useState<QuestionInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [startingAttempt, setStartingAttempt] = useState(false);
  const startLockRef = useRef(false);

  if (testQuery.error && !testQuery.waiting) {
    return <ErrorState title={t("tests.loadTestFailed")} onRetry={testQuery.retry} />;
  }

  if (!aggregate) {
    return (
      <div className="mmm-test-screen">
        <QueryStatus queries={[testQuery]} />
        <div className="mmm-card" style={{ padding: 20 }}>
          <LoadingRows rows={5} />
        </div>
      </div>
    );
  }
  const { test, questions, attempts } = aggregate;

  const startEditing = () => {
    setDraftQuestions(toEditableQuestions(questions));
    setError(null);
    setEditing(true);
  };

  const addQuestion = () => {
    setDraftQuestions((prev) => [
      ...prev,
      {
        questionNumber: prev.length + 1,
        type: "multipleChoice",
        prompt: "",
        correctAnswer: null,
        explanation: "",
        answerOptions: [
          { sortOrder: 10, text: t("tests.optionA"), isCorrect: true },
          { sortOrder: 20, text: t("tests.optionB"), isCorrect: false },
        ],
        rubricItems: [],
      },
    ]);
  };

  const updateQuestion = (index: number, updated: QuestionInput) => {
    setDraftQuestions((prev) => prev.map((q, i) => (i === index ? updated : q)));
  };

  const removeQuestion = (index: number) => {
    setDraftQuestions((prev) => prev.filter((_, i) => i !== index).map((q, i) => ({ ...q, questionNumber: i + 1 })));
  };

  const handleSave = async () => {
    try {
      await replaceAITestQuestions({ testId, questions: draftQuestions });
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiRequestError) setError(err.message);
      // Other errors (network unreachable, expired session, ...) are already
      // toasted by commands.ts's run() — the Button's onClick invokes this
      // without awaiting it, so this must not rethrow.
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: t("tests.deleteTest"),
      message: t("tests.deleteTestConfirm", { title: test.title }),
      confirmLabel: t("tests.deleteTest"),
    });
    if (!ok) return;
    try {
      await deleteAITest({ testId });
      navigate(`/subjects/${subject.id}/tests`);
    } catch {
      // Already toasted — the Button's onClick invokes this without awaiting it.
    }
  };

  const handleStartAttempt = async () => {
    if (startLockRef.current) return;
    startLockRef.current = true;
    setStartingAttempt(true);
    // Already toasted on failure — the Button's onClick invokes this without
    // awaiting it, so a rejection here must not escape uncaught.
    try {
      const result = await startTestAttempt({ testId });
      navigate(`/subjects/${subject.id}/tests/${testId}/attempts/${result.attempt.id}`);
    } catch {
      // Nothing left to do.
    } finally {
      startLockRef.current = false;
      setStartingAttempt(false);
    }
  };

  const hasOpenAttempt = attempts.some((attempt) => attempt.status === "inProgress" || attempt.status === "submitted");
  const sortedQuestions = questions.slice().sort((a, b) => a.questionNumber - b.questionNumber);
  const handleViewChange = async (nextView: TestView) => {
    if (nextView === "answers" && view !== "answers") {
      const ok = await confirm({ title: t("tests.answers"), message: t("tests.revealAnswersWarning"), confirmLabel: t("tests.answers") });
      if (!ok) return;
    }
    setView(nextView);
  };

  return (
    <div className="mmm-test-screen">
      <div className="mmm-test-header">
        <TestTitleField key={test.id} testId={testId} title={test.title} chapterId={test.chapterId} />
        <div className="mmm-test-actions">
          <Button onClick={() => navigate(test.chapterId
            ? `/subjects/${subject.id}/chapters/${test.chapterId}/tests`
            : `/subjects/${subject.id}/tests`)}>
            <Icon name="collapse" />{t("tests.backToTests")}
          </Button>
          {!editing && attempts.length === 0 && (
            <Button variant="secondary" onClick={startEditing}>
              <Icon name="edit" />
              {t("tests.editQuestions")}
            </Button>
          )}
          <ExportPreview
            options={<Checkbox label={t("tests.includeMemorandum")} checked={includeMemorandum} onChange={(event) => setIncludeMemorandum(event.target.checked)} />}
            preview={<TestDocumentPreview subjectTitle={subject.title} test={test} questions={sortedQuestions} includeMemorandum={includeMemorandum} />}
            onWord={() => downloadTestDocument({ testId, format: "docx", includeMemorandum })}
            onPdf={() => downloadTestDocument({ testId, format: "pdf", includeMemorandum })}
          />
          {!hasOpenAttempt && (
            <Button variant="gold" disabled={startingAttempt} aria-busy={startingAttempt} onClick={handleStartAttempt}>
              {t("tests.startAttempt")}
            </Button>
          )}
          <Button variant="danger" onClick={handleDelete}>
            <Icon name="trash" />
            {t("tests.deleteTest")}
          </Button>
        </div>
      </div>

      {editing ? (
        <div className="mmm-test-column">
          {draftQuestions.map((question, index) => (
            <QuestionEditor
              key={index}
              question={question}
              onChange={(updated) => updateQuestion(index, updated)}
              onDelete={() => removeQuestion(index)}
            />
          ))}
          <Button variant="secondary" onClick={addQuestion}>
            {t("tests.addQuestion")}
          </Button>

          {error && <p className="mmm-form-error">{error}</p>}

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button variant="primary" onClick={handleSave}>
              {t("tests.saveQuestions")}
            </Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="mmm-test-column">
          <div className="mmm-test-viewbar">
            <SegmentedControl
              options={VIEW_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
              value={view}
              onChange={(nextView) => void handleViewChange(nextView)}
            />
            <span className="mmm-test-count">
              {t("tests.questionsCount", { count: sortedQuestions.length })}
            </span>
          </div>

          {sortedQuestions.map((question) => (
            <QuestionCard key={question.id} question={question} showAnswers={view === "answers"} />
          ))}
        </div>
      )}

      <div className="mmm-card mmm-attempts-card">
        <div className="mmm-attempts-head">
          <h2>{t("tests.attempts")}</h2>
        </div>
        {attempts.length === 0 ? (
          <p style={{ color: "var(--mmm-muted)", margin: 0 }}>{t("tests.noAttempts")}</p>
        ) : (
          attempts.map((attempt) => {
            const status = ATTEMPT_STATUS[attempt.status];
            return <AttemptRow key={attempt.id} attempt={attempt} status={status} subjectId={subject.id} testId={testId} navigate={navigate} />;
          })
        )}
      </div>
    </div>
  );
}
